import React, {
  useEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { useT } from "../helpers/useT";
import {
  constructAthenaUrl,
  constructApiUrl,
  ListResponseWithIncluded,
  Resource,
  SingleResponse,
  useFetch,
} from "../api/useFetch";
import { useLaunchDarklyFlags } from "../launchdarkly/ClientSideLDProvider";
import { MARKETPLACE_FEATURE_FLAG } from "../launchdarkly/useLaunchDarkly";
import { transformMarketplaceToJsonApi } from "./marketplaceAdapter";
import { MarketplaceOffersResponse } from "./marketplaceTypes";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableHeaderRow,
  TableRow,
} from "../components/List/Table";
import { Headline } from "../components/Headline";
import { EmptyListPlaceholder } from "../components/List/EmptyList";
import { ListFilters, Pagination } from "../components/List/List";
import { Location, Offer } from "./offer";
import { DistanceOrDuration } from "./components/DistanceOrDuration";
import CustomDate from "../components/Date";
import { ApiResponseAware } from "../api/ApiResponseAware";
import { tabs } from "./ride";
import Tabs from "../components/Tabs";
import { useMe } from "../me";
import Price from "../components/Price";
import { ServiceClassWithTitle } from "../components/ServiceClass";
import RefreshList from "../list/Refresh";
import PropertyWithTitle from "../components/PropertyWithTitle";
import { OfferDialog } from "./components/OfferDialog";
import { useAcceptOffer } from "./hooks";
import { SkeletonPage, SkeletonTable } from "../components/Skeleton";
import RawFormattedMessage from "../components/RawFormattedMessage";

export type OffersResponse = ListResponseWithIncluded<Offer, Location>;
export type OfferResponse = SingleResponse<Offer>;

type SelectedOffer = {
  offer: Resource<Offer>;
  pickupAddress: string | undefined;
  dropoffAddress: string | undefined;
};

export function OfferList() {
  const me = useMe();
  const t = useT();
  const [currentPage, setCurrentPage] = useState(1);
  const [isMounted, setIsMounted] = useState(false);
  const { flags, isReady } = useLaunchDarklyFlags();
  const marketplaceFlag = flags[MARKETPLACE_FEATURE_FLAG] || null;

  // Cache previous transformed data to prevent unmounting during reload
  const prevTransformedDataRef = useRef<OffersResponse | null>(null);

  // Dialog state at parent level - preserved during OffersTable unmount/remount
  const [selectedOffer, setSelectedOffer] = useState<SelectedOffer | null>(
    null,
  );
  const { acceptOffer, isAccepting } = useAcceptOffer();

  const handleCloseDialog = useCallback(() => setSelectedOffer(null), []);

  const handleOfferClick = useCallback(
    (
      offer: Resource<Offer>,
      pickupAddress: string | undefined,
      dropoffAddress: string | undefined,
    ) => {
      setSelectedOffer({ offer, pickupAddress, dropoffAddress });
    },
    [],
  );

  // Callback to notify OffersTable when an offer is accepted
  const [lastAcceptedOfferId, setLastAcceptedOfferId] = useState<string | null>(
    null,
  );

  const handleAccept = useCallback(
    async (offer: Resource<Offer>) => {
      const result = await acceptOffer(offer);
      setSelectedOffer(null);

      // Notify OffersTable to update goneOfferIds
      if (
        result.success ||
        result.reason === "already_accepted" ||
        result.reason === "offer_canceled"
      ) {
        setLastAcceptedOfferId(offer.id);
      }

      // Trigger immediate refresh when offer is no longer available
      // This happens when:
      // 1. offer_canceled (404): Offer was canceled by admin or guest
      // 2. already_accepted (409/422): Offer was accepted by another chauffeur
      // In both cases, refreshing ensures user sees updated offers immediately
      if (
        result.reason === "offer_canceled" ||
        result.reason === "already_accepted"
      ) {
        // Small delay to avoid race condition where the backend might not
        // have fully processed the change yet
        setTimeout(() => {
          reloadRef.current();
        }, 500);
      }

      return result;
    },
    [acceptOffer],
  );

  // Detect client-side mount to prevent SSR hydration mismatch
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Conditionally construct URL based on feature flag
  // - "marketplace": Use marketplace API (through partner-portal-api gateway)
  // - "auctions" or other: Use Hades API
  const useMarketplace = marketplaceFlag === "marketplace";

  // Don't make API call until:
  // 1. LaunchDarkly flags are ready (isReady)
  // 2. User data is loaded (me.hasFetched) - CRITICAL FIX for post-login data loading
  // This prevents making API calls with empty/invalid headers (useFetch bug - see options comment below)
  const url =
    !isReady || !me.hasFetched
      ? null // useFetch handles null URL by not making request
      : useMarketplace
        ? constructApiUrl("/api/v1/chauffeur/offers", {
            page: `${currentPage}`,
            page_size: "30",
          })
        : constructAthenaUrl("/hades/offers", {
            "page[number]": `${currentPage}`,
            "page[size]": "30",
            include: "pickup_location,dropoff_location",
          });

  // IMPORTANT: These headers MUST be set before the API call is made
  // The useFetch hook does NOT react to changes in options (bug in useFetch.ts:94 - options not in dependency array)
  // That's why we wait for me.hasFetched before setting url (see above)
  //
  // TEMPORARY WORKAROUND: Manually add headers for marketplace API
  // The API Gateway authorizer is not properly populating x-user-lsp-id and x-user-bd-id
  // TODO: Remove once infrastructure authorizer is fixed to populate all required context values
  const options =
    useMarketplace && isReady
      ? {
          headers: {
            "x-user-id": me.id,
            "x-user-roles": me.roles.join(","),
            "x-user-lsp-id": me.lsp?.id || "",
            "x-user-bd-id": me.lsp?.businessDistrict?.uuid || "",
          },
        }
      : {};

  const apiCall = useFetch(url, options);

  // Store latest reload function in ref to avoid stale closures in interval
  const reloadRef = useRef(apiCall.reload);

  // Keep ref updated with latest reload function
  useEffect(() => {
    reloadRef.current = apiCall.reload;
  }, [apiCall.reload]);

  // Transform response if using marketplace API
  const transformedData = useMemo(() => {
    // During reload, return previous data to keep component mounted
    if (!apiCall.data || apiCall.isLoading) {
      return prevTransformedDataRef.current;
    }

    // Only transform if flags are ready to avoid processing data with wrong assumptions
    if (!isReady) {
      return prevTransformedDataRef.current;
    }

    // Only transform if using marketplace API
    if (useMarketplace) {
      // Validate that the data is actually marketplace format (has 'items' property)
      const hasMarketplaceStructure =
        typeof apiCall.data === "object" && "items" in apiCall.data;

      if (!hasMarketplaceStructure) {
        // Data doesn't match marketplace format - likely stale data from previous API
        // Return cached data to keep component mounted
        console.warn(
          "[OfferList] Data structure mismatch - expected marketplace format",
        );
        return prevTransformedDataRef.current;
      }

      try {
        const transformed = transformMarketplaceToJsonApi(
          apiCall.data as MarketplaceOffersResponse,
        );
        prevTransformedDataRef.current = transformed; // Cache result
        return transformed;
      } catch (error) {
        console.error(
          "[OfferList] Error transforming marketplace data:",
          error,
        );
        return prevTransformedDataRef.current;
      }
    }

    // Hades API returns JSON:API format directly
    // Validate that the data has the expected JSON:API structure (has 'data' property)
    const hasJsonApiStructure =
      typeof apiCall.data === "object" && "data" in apiCall.data;

    if (!hasJsonApiStructure) {
      // Data doesn't match JSON:API format - likely stale data from previous API
      // Return cached data to keep component mounted
      console.warn(
        "[OfferList] Data structure mismatch - expected JSON:API format",
      );
      return prevTransformedDataRef.current;
    }

    const result = apiCall.data as OffersResponse;
    prevTransformedDataRef.current = result; // Cache result
    return result;
  }, [apiCall.data, apiCall.isLoading, useMarketplace, isReady]);

  // Auto-refresh offers list every 15 seconds for both marketplace and auction offers
  useEffect(() => {
    // Don't start interval until we have:
    // 1. Valid URL (flags ready, user data loaded)
    // 2. Initial data loaded (prevents refresh during loading state)
    if (!url || !transformedData) {
      return;
    }

    const intervalId = setInterval(() => {
      reloadRef.current(); // Call via ref to avoid stale closure
    }, 15000); // 15 seconds

    // Cleanup: Clear interval on unmount or when dependencies change
    return () => {
      clearInterval(intervalId);
    };
  }, [url, transformedData]); // Restart interval when URL or data changes

  // Show skeleton loading state during SSR and until LaunchDarkly is ready
  // This prevents hydration mismatch by ensuring consistent initial render
  const isInitializing = !isMounted || !isReady;
  if (isInitializing) {
    return <SkeletonPage />;
  }

  // Show skeleton table while loading initial data (prevents layout shift)
  // This includes waiting for me data to load (me.hasFetched)
  if ((apiCall.isLoading || !me.hasFetched) && !transformedData) {
    return (
      <>
        <Headline title={t("rides.title")} flex={true}>
          <RefreshList action={() => apiCall.reload()} />
        </Headline>
        <Tabs tabs={tabs(me)} />
        <SkeletonTable />
      </>
    );
  }

  return (
    <>
      <Headline title={t("rides.title")} flex={true}>
        <RefreshList action={() => apiCall.reload()} />
      </Headline>
      <Tabs tabs={tabs(me)} />
      <ApiResponseAware
        apiCall={{ ...apiCall, data: transformedData }}
        render={(response: OffersResponse) => (
          <OffersTable
            response={response}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            onOfferClick={handleOfferClick}
            lastAcceptedOfferId={lastAcceptedOfferId}
          />
        )}
      />
      <OfferDialog
        offer={selectedOffer?.offer ?? null}
        pickupAddress={selectedOffer?.pickupAddress}
        dropoffAddress={selectedOffer?.dropoffAddress}
        isOpen={selectedOffer !== null}
        onClose={handleCloseDialog}
        onAccept={handleAccept}
        isAccepting={isAccepting}
      />
    </>
  );
}

function OffersTable({
  response,
  currentPage,
  setCurrentPage,
  onOfferClick,
  lastAcceptedOfferId,
}: {
  response: OffersResponse;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  onOfferClick: (
    offer: Resource<Offer>,
    pickupAddress: string | undefined,
    dropoffAddress: string | undefined,
  ) => void;
  lastAcceptedOfferId: string | null;
}) {
  const t = useT();
  const [goneOfferIds, setGoneOfferIds] = useState<string[]>([]);

  // Update goneOfferIds when an offer is accepted
  useEffect(() => {
    if (lastAcceptedOfferId) {
      setGoneOfferIds((prevState) => [...prevState, lastAcceptedOfferId]);
    }
  }, [lastAcceptedOfferId]);

  const offers = (response?.data ?? []).filter(
    (o) => !goneOfferIds.includes(o.id),
  );
  const offersRemovedCount = (response?.data?.length ?? 0) - offers.length;

  return (
    <>
      <ListFilters
        recordCount={t("offers.record-count", {
          number: (response?.meta?.record_count ?? 0) - offersRemovedCount,
        })}
      />
      <EmptyListPlaceholder data={offers} title={t("offers.empty-list")}>
        <Table>
          <TableHead>
            <TableHeaderRow>
              <TableHeaderCell width={160}>{t("shared.date")}</TableHeaderCell>
              <TableHeaderCell>{t("shared.pickup")}</TableHeaderCell>
              <TableHeaderCell>{t("shared.dropoff")}</TableHeaderCell>
              <TableHeaderCell width={90}>
                {t("shared.service-class")}
              </TableHeaderCell>
              <TableHeaderCell width={80}>{t("shared.price")}</TableHeaderCell>
            </TableHeaderRow>
          </TableHead>
          <TableBody>
            {offers.map((offer) => (
              <OfferRow
                key={offer.id}
                originalOffer={offer}
                included={response.included}
                onOfferClick={onOfferClick}
              >
                <RawFormattedMessage id="shared.details" />
              </OfferRow>
            ))}
          </TableBody>
        </Table>
        <Pagination
          currentPage={currentPage}
          pageCount={response?.meta?.page_count ?? 0}
          onPageClick={setCurrentPage}
        />
      </EmptyListPlaceholder>
    </>
  );
}

function OfferRow({
  originalOffer,
  included,
  onOfferClick,
  children,
}: {
  originalOffer: Resource<Offer>;
  included: Array<Resource<Location>> | undefined;
  children: React.ReactNode;
  onOfferClick: (
    offer: Resource<Offer>,
    pickupAddress: string | undefined,
    dropoffAddress: string | undefined,
  ) => void;
}) {
  const pickupAddress = included?.find(
    (l) => l.id === originalOffer.relationships?.pickup_location?.data.id,
  )?.attributes.formatted_address_en;

  const dropoffAddress = included?.find(
    (l) => l.id === originalOffer.relationships?.dropoff_location?.data.id,
  )?.attributes.formatted_address_en;

  const handleClick = () => {
    onOfferClick(originalOffer, pickupAddress, dropoffAddress);
  };

  return (
    <TableRow key={originalOffer.id}>
      <TableCell>
        <PropertyWithTitle isList title="shared.date">
          <CustomDate
            value={originalOffer.attributes.starts_at}
            isHighlighted
          />
        </PropertyWithTitle>
      </TableCell>
      <TableCell>
        <PropertyWithTitle isList title="shared.pickup">
          {pickupAddress}
        </PropertyWithTitle>
      </TableCell>
      <TableCell>
        <PropertyWithTitle isList title="shared.dropoff">
          {dropoffAddress}
        </PropertyWithTitle>
      </TableCell>
      <TableCell>
        <ServiceClassWithTitle id={originalOffer.attributes.service_class} />
      </TableCell>
      <TableCell>
        <PropertyWithTitle isList title="shared.price">
          <Price
            currency={originalOffer.attributes.currency}
            value={parseFloat(originalOffer.attributes.price)}
          />
          <DistanceOrDuration ride={originalOffer.attributes} />
          <button
            type="button"
            data-type="tertiary"
            onClick={handleClick}
            aria-label="View offer details"
          >
            {children}
          </button>
        </PropertyWithTitle>
      </TableCell>
    </TableRow>
  );
}
